// The `.svelte.ts` name lets the svelte plugin compile this file so the
// `$state` rune is available for reactive placeholder props.
import { mount, unmount } from 'svelte';
// Maps the wrapper's component props to the core's DOM-factory prop names.
const FACTORY_PROP_NAMES = {
    loadingComponent: 'getLoadingComponent',
    errorComponent: 'getErrorComponent',
    noDataComponent: 'getNoDataComponent',
    noSizeComponent: 'getNoSizeComponent',
    noSeriesComponent: 'getNoSeriesComponent',
    configErrorComponent: 'getConfigErrorComponent'
};
class PlaceholderSlot {
    component;
    container;
    factory;
    mounted = null;
    instance = null;
    props = $state({});
    componentContext;
    constructor(component, componentContext) {
        this.component = component;
        this.componentContext = componentContext;
        this.container = document.createElement('div');
        // The container is a neutral wrapper; the placeholder component owns layout.
        this.container.style.display = 'contents';
        this.factory = (context) => this.render(context);
    }
    render(context) {
        for (const key of Object.keys(this.props)) {
            if (!(key in context)) {
                delete this.props[key];
            }
        }
        Object.assign(this.props, context);
        this.mountCurrent();
        return this.container;
    }
    /** Mounted or unmounted to match whether the core still holds the container in the chart. */
    syncAttached(host) {
        const attached = host.contains(this.container);
        if (attached && !this.instance && this.mounted !== null) {
            this.mountCurrent();
        }
        else if (!attached && this.instance) {
            void unmount(this.instance);
            this.instance = null;
        }
    }
    /** Mount the current component, replacing an instance of a previous one. */
    mountCurrent() {
        if (this.instance && this.mounted !== this.component) {
            void unmount(this.instance);
            this.instance = null;
        }
        if (!this.instance) {
            // the chart component's contexts, so placeholders can read app-level providers
            this.instance = mount(this.component, { target: this.container, props: this.props, context: this.componentContext });
            this.mounted = this.component;
        }
    }
    /** A component change remounts a slot the core has already rendered; the core's factory gate would not re-run for it. */
    setComponent(component) {
        if (this.component !== component) {
            this.component = component;
            if (this.instance) {
                this.mountCurrent();
            }
        }
    }
    destroy() {
        if (this.instance) {
            void unmount(this.instance);
            this.instance = null;
        }
    }
}
/**
 * Adapts placeholder component props into the DOM-node factories the core
 * expects: each slot keeps one persistent container div holding a mounted
 * instance whose reactive props are updated on repeat factory calls. The
 * factory identity is stable per slot; a component change flows through
 * `transform` and remounts a slot the core has already rendered.
 */
export function createPlaceholderAdapter(componentContext) {
    const slots = new Map();
    // Unmounts a slot's instance and forgets it; a later prop gets a fresh slot.
    function releaseSlot(propName) {
        const slot = slots.get(propName);
        if (!slot) {
            return;
        }
        slot.destroy();
        slots.delete(propName);
    }
    return {
        transform(props) {
            const out = { ...props };
            for (const propName of Object.keys(FACTORY_PROP_NAMES)) {
                const component = out[propName];
                delete out[propName];
                if (component) {
                    let slot = slots.get(propName);
                    if (!slot) {
                        slot = new PlaceholderSlot(component, componentContext);
                        slots.set(propName, slot);
                    }
                    slot.setComponent(component);
                    out[FACTORY_PROP_NAMES[propName]] = slot.factory;
                }
                else {
                    // the chart falls back to its built-in placeholder, so nothing keeps this instance alive
                    releaseSlot(propName);
                }
            }
            return out;
        },
        attach(host) {
            if (typeof MutationObserver === 'undefined') {
                return () => { };
            }
            const observer = new MutationObserver(() => {
                for (const slot of slots.values()) {
                    slot.syncAttached(host);
                }
            });
            observer.observe(host, { childList: true, subtree: true });
            return () => observer.disconnect();
        },
        destroy() {
            for (const propName of [...slots.keys()]) {
                releaseSlot(propName);
            }
        }
    };
}
